import { Box } from '@mui/material';
import { DataGridProProps, GRID_DETAIL_PANEL_TOGGLE_FIELD, GridRowParams } from '@mui/x-data-grid-pro';
import { t } from 'i18next';
import { ProjectExceptionType } from '../../../project-exceptions/enums/ProjectExceptionType';
import { ProjectExceptionRepository } from '../../../project-exceptions/services/ProjectExceptionRepository';
import { BlankCell } from '../../components/Cells/BlankCell';
import { ExpandCell } from '../../components/Cells/ExpandCell';
import { FilterConfiguration } from '../../components/FilterPanel/types';
import { TypographyOverflow } from '../../components/TypographyOverflow';
import { PaginatedTableController, PaginatedTableState, PrefilledDataConfig } from '../../controllers/PaginatedTableController';
import { TRACE_TYPES_WITH_EXCEPTIONS, TraceType } from '../../enums/TraceType';
import { DefaultHeaderConfiguration, FieldConfiguration } from '../../types/paginated-table';
import { BLANK_PAGINATION_RESULT, CursorInfo, PaginationResult } from '../../types/pagination';
import { DateUtils } from '../../utils/DateUtils';
import { ModelType } from '../../utils/GQLIdEncoder';
import { bind } from '../../utils/decorators';
import { CallDetails } from '../components/CallDetails';
import { Call } from '../models/Call';
import { CallDetail } from '../models/CallDetail';
import { CallRepository } from '../services/CallRepository';

type Props = {
  variant: ModelType.Instance | ModelType.Operation;
  variantId: number;
  defaultPageSize: number;

  projectId?: number;
  traceType?: TraceType;

  initialCalls?: PaginationResult<Call> | null;

  parentLoading: boolean;
};

type State = PaginatedTableState & {
  openCallIds: number[];
  canCreateExceptions: boolean | null;
};

export class CallsTableController extends PaginatedTableController<Call, Props, State> {
  private readonly repository = new CallRepository();
  private readonly exceptionRepository = new ProjectExceptionRepository();

  static defaultProps = {
    initialCalls: null,
  };

  getBaseState(): State {
    return { ...super.getBaseState(), openCallIds: [], canCreateExceptions: null };
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
    super.componentDidUpdate(prevProps, prevState);

    const { projectId, traceType } = this.props;
    const { canCreateExceptions } = this.state;

    if (canCreateExceptions === null && projectId && traceType && TRACE_TYPES_WITH_EXCEPTIONS.includes(traceType)) {
      // Only evaluate the permission to create exceptions if the trace type can have them
      this.permissionsService.canCreateException(projectId).then((granted: boolean) => {
        this.setState({ canCreateExceptions: granted });
      });
    }
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    this.repository.cancelAllRequests();
    this.exceptionRepository.cancelAllRequests();
  }

  protected isLoading(): boolean {
    return super.isLoading() || this.props.parentLoading;
  }

  protected getPrefilledConfig() {
    return {
      prefillActive: true,
      initialLoading: this.props.parentLoading,
      initialData: this.props.initialCalls,
    } as PrefilledDataConfig<Call>;
  }

  protected getDefaultPageSize(): number {
    return this.props.defaultPageSize;
  }

  protected getAdditionalDataGridProps(): Partial<DataGridProProps<Call>> {
    const { openCallIds } = this.state;
    return {
      ...super.getAdditionalDataGridProps(),
      nonce: openCallIds.join('_'),
      getDetailPanelHeight: () => 'auto',
      detailPanelExpandedRowIds: openCallIds,
      getDetailPanelContent: this.renderExpandRow,
    };
  }

  protected getDefaultHeaderConfigurations(): DefaultHeaderConfiguration {
    return { title: t('calls') };
  }

  protected renderTable(tableComponent: React.ReactElement) {
    return <Box className="instance-calls">{tableComponent}</Box>;
  }

  protected async fetchData(cursorInfo: CursorInfo): Promise<PaginationResult<Call>> {
    try {
      return await this.repository.getCalls(this.props.variant, this.props.variantId, cursorInfo);
    } catch (e: unknown) {
      this.handleError(e);
      return BLANK_PAGINATION_RESULT;
    }
  }

  protected getFieldConfigurations(): FieldConfiguration<Call>[] {
    const { openCallIds } = this.state;
    return [
      {
        fieldKey: GRID_DETAIL_PANEL_TOGGLE_FIELD,
        label: '',
        columnProps: { maxWidth: 52, minWidth: 52 },
        render: (item, { tabIndex }) => (
          <ExpandCell
            tabIndex={tabIndex}
            expanded={openCallIds.includes(item.getId())}
            disabled={false}
            onToggle={() => this.toggleCall(item)}
            openLabel={t('a11y.report.issues.callAccordionClosed', { callId: item.getId() }) as string}
            closeLabel={t('a11y.report.issues.callAccordionOpen', { callId: item.getId() }) as string}
          />
        ),
      },
      {
        fieldKey: 'timestamp',
        label: t('timestamp'),
        render: (item, { hasFocus }) => {
          const timestamp = item.getTimestamp();
          if (timestamp) {
            return <TypographyOverflow hasFocus={hasFocus}>{DateUtils.toTimestampString(timestamp)}</TypographyOverflow>;
          }
          return <BlankCell blankText={t('unknown')} />;
        },
      },
      {
        fieldKey: 'keptByFilters',
        label: t('keptByFilters'),
        columnProps: {
          maxWidth: 150,
        },
        render: (item) => {
          if (item.isKeptByFilters()) {
            return t('yes');
          }
          return t('no');
        },
      },
    ];
  }

  protected getFilterConfigurations(): FilterConfiguration[] {
    return [];
  }

  private async handleCreateException(term: string, projectId: number, traceType: TraceType) {
    try {
      return await this.exceptionRepository.createException(projectId, ProjectExceptionType.BLACKLIST, term, null, true, traceType);
    } catch (e: unknown) {
      this.handleError(e);
      return null;
    }
  }

  private async loadCall(callId: number): Promise<CallDetail | null> {
    try {
      return await this.repository.getCallDetail(callId);
    } catch (e: unknown) {
      this.handleError(e);
      return null;
    }
  }

  private toggleCall(call: Call) {
    const { openCallIds } = this.state;
    if (openCallIds.includes(call.getId())) {
      this.setState({ openCallIds: openCallIds.filter((i) => i !== call.getId()) });
    } else {
      this.setState({ openCallIds: [...openCallIds, call.getId()] });
    }
  }

  @bind
  private renderExpandRow({ row }: GridRowParams<Call>) {
    const { traceType, projectId } = this.props;
    const canCreateExceptions = this.state.canCreateExceptions && traceType && projectId && row.isKeptByFilters();
    return (
      <Box sx={{ p: 2, backgroundColor: 'backdrops.expandedRow' }}>
        <CallDetails
          onInit={() => this.loadCall(row.getId())}
          onCreateException={canCreateExceptions ? (term) => this.handleCreateException(term, projectId, traceType) : undefined}
        />
      </Box>
    );
  }
}
